import { Area, AreaChart, Bar, BarChart, CartesianGrid, Line, LineChart, ReferenceLine, XAxis, YAxis } from "recharts"

import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
  cn,
  type ChartConfig,
} from "@ibada/ui"

/**
 * Value formatting is INJECTED, not imported.
 *
 * These charts previously reached into the tenant's `overview-format`, which carries ADR-0024 branch-autonomy
 * ratio semantics — meaning that module decides whether a figure is money or a percentage. A shared chart must
 * not carry one app's disclosure rules, so the caller supplies both formatters. Same convention as
 * `OverviewIncomeExpenseTrendChartWidget`, which already takes `formatMoney`.
 *
 * Deliberately NOT named "money": the same shapes plot counts on the platform board, and a prop called
 * `formatMoney` receiving a workspace count is how a chart ends up labelling "42 churches" as "$42.00".
 * `unit` is whatever the caller's formatters need — a currency code for money, ignored for counts.
 */
export type ChartValueFormatters = {
  /** Full precision, for tooltips. */
  formatValue: (value: number, unit: string) => string
  /** Compact, for axis ticks (e.g. "$1.2k", "1.2k"). */
  formatCompact: (value: number, unit: string) => string
}

export interface DashboardChartSeries {
  key: string
  label: string
  color: string
}

/** Shared axis/grid defaults tuned for compact one-column cards. */
const AXIS_FONT = 11

function buildConfig(series: DashboardChartSeries[]): ChartConfig {
  return Object.fromEntries(series.map((s) => [s.key, { label: s.label, color: s.color }])) as ChartConfig
}

function valueTooltip(unit: string, formatValue: ChartValueFormatters["formatValue"]) {
  return (
    <ChartTooltipContent
      indicator="dot"
      formatter={(value, name) => (
        <div className="flex w-full items-center justify-between gap-3">
          <span className="text-muted-foreground">{name}</span>
          <span className="font-medium tabular-nums text-foreground">
            {formatValue(typeof value === "number" ? value : Number(value), unit)}
          </span>
        </div>
      )}
    />
  )
}

export interface DashboardGroupedBarChartProps {
  data: Array<Record<string, string | number>>
  xKey: string
  series: DashboardChartSeries[]
  currency: string
  height?: number
  showYAxis?: boolean
  className?: string
}

/**
 * Grouped bar chart for period comparisons (e.g. money-in vs money-out per bucket). Compact by design:
 * faint horizontal grid only, sparse x labels, currency tooltips, no decorative animation.
 */
export function DashboardGroupedBarChart({
  data,
  xKey,
  series,
  currency,
  height = 168,
  showYAxis = false,
  className,
  formatValue,
  formatCompact,
}: DashboardGroupedBarChartProps & ChartValueFormatters) {
  return (
    <ChartContainer config={buildConfig(series)} className={cn("aspect-auto w-full", className)} style={{ height }}>
      <BarChart data={data} margin={{ left: 4, right: 4, top: 4, bottom: 0 }} barGap={2} barCategoryGap="20%">
        <CartesianGrid vertical={false} strokeOpacity={0.4} />
        <XAxis
          dataKey={xKey}
          tickLine={false}
          axisLine={false}
          tickMargin={6}
          minTickGap={16}
          fontSize={AXIS_FONT}
        />
        {showYAxis ? (
          <YAxis
            width={44}
            tickLine={false}
            axisLine={false}
            fontSize={AXIS_FONT}
            tickFormatter={(value) => formatCompact(Number(value), currency)}
          />
        ) : null}
        <ChartTooltip cursor={{ opacity: 0.08 }} content={valueTooltip(currency, formatValue)} />
        {series.map((s) => (
          <Bar key={s.key} dataKey={s.key} fill={`var(--color-${s.key})`} radius={4} isAnimationActive={false} />
        ))}
      </BarChart>
    </ChartContainer>
  )
}

export interface DashboardAreaChartProps {
  data: Array<Record<string, string | number>>
  xKey: string
  series: DashboardChartSeries
  currency: string
  height?: number
  showYAxis?: boolean
  className?: string
}

export interface DashboardDivergingAreaChartProps {
  data: Array<Record<string, string | number>>
  xKey: string
  positiveSeries: DashboardChartSeries
  negativeSeries: DashboardChartSeries
  currency: string
  height?: number
  className?: string
}

/**
 * Diverging area chart for movement comparisons: positive flow above the baseline, negative flow below it.
 * Useful when the user needs to read direction and pressure over time, not compare exact grouped bars.
 */
export function DashboardDivergingAreaChart({
  data,
  xKey,
  positiveSeries,
  negativeSeries,
  currency,
  height = 168,
  className,
  formatValue,
  formatCompact,
}: DashboardDivergingAreaChartProps & ChartValueFormatters) {
  const positiveFillId = `dash-diverging-${positiveSeries.key}`
  const negativeFillId = `dash-diverging-${negativeSeries.key}`

  return (
    <ChartContainer
      config={buildConfig([positiveSeries, negativeSeries])}
      className={cn("aspect-auto w-full", className)}
      style={{ height }}
    >
      <AreaChart data={data} margin={{ left: 4, right: 4, top: 8, bottom: 0 }}>
        <defs>
          <linearGradient id={positiveFillId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={`var(--color-${positiveSeries.key})`} stopOpacity={0.32} />
            <stop offset="100%" stopColor={`var(--color-${positiveSeries.key})`} stopOpacity={0.04} />
          </linearGradient>
          <linearGradient id={negativeFillId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={`var(--color-${negativeSeries.key})`} stopOpacity={0.04} />
            <stop offset="100%" stopColor={`var(--color-${negativeSeries.key})`} stopOpacity={0.3} />
          </linearGradient>
        </defs>
        <CartesianGrid vertical={false} strokeOpacity={0.28} />
        <ReferenceLine y={0} stroke="var(--border)" strokeOpacity={0.7} />
        <XAxis
          dataKey={xKey}
          tickLine={false}
          axisLine={false}
          tickMargin={6}
          minTickGap={24}
          fontSize={AXIS_FONT}
        />
        <YAxis hide domain={["dataMin", "dataMax"]} />
        <ChartTooltip
          cursor={{ opacity: 0.08 }}
          content={
            <ChartTooltipContent
              indicator="dot"
              formatter={(value, name) => (
                <div className="flex w-full items-center justify-between gap-3">
                  <span className="text-muted-foreground">{name}</span>
                  <span className="font-medium tabular-nums text-foreground">
                    {formatValue(Math.abs(typeof value === "number" ? value : Number(value)), currency)}
                  </span>
                </div>
              )}
            />
          }
        />
        <Area
          dataKey={positiveSeries.key}
          type="monotone"
          stroke={`var(--color-${positiveSeries.key})`}
          strokeWidth={2}
          fill={`url(#${positiveFillId})`}
          isAnimationActive={false}
          dot={false}
        />
        <Area
          dataKey={negativeSeries.key}
          type="monotone"
          stroke={`var(--color-${negativeSeries.key})`}
          strokeWidth={2}
          fill={`url(#${negativeFillId})`}
          isAnimationActive={false}
          dot={false}
        />
      </AreaChart>
    </ChartContainer>
  )
}

/**
 * Single-series area chart for one continuous value over time (e.g. resulting cash balance). One smooth
 * line, subtle fill, currency tooltips, minimal axes — never multiple overlapping areas.
 */
export function DashboardAreaChart({
  data,
  xKey,
  series,
  currency,
  height = 168,
  showYAxis = false,
  className,
  formatValue,
  formatCompact,
}: DashboardAreaChartProps & ChartValueFormatters) {
  const fillId = `dash-area-${series.key}`
  return (
    <ChartContainer config={buildConfig([series])} className={cn("aspect-auto w-full", className)} style={{ height }}>
      <AreaChart data={data} margin={{ left: 4, right: 4, top: 4, bottom: 0 }}>
        <defs>
          <linearGradient id={fillId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={`var(--color-${series.key})`} stopOpacity={0.28} />
            <stop offset="100%" stopColor={`var(--color-${series.key})`} stopOpacity={0.02} />
          </linearGradient>
        </defs>
        <CartesianGrid vertical={false} strokeOpacity={0.4} />
        <XAxis
          dataKey={xKey}
          tickLine={false}
          axisLine={false}
          tickMargin={6}
          minTickGap={24}
          fontSize={AXIS_FONT}
        />
        {showYAxis ? (
          <YAxis
            width={44}
            tickLine={false}
            axisLine={false}
            fontSize={AXIS_FONT}
            tickFormatter={(value) => formatCompact(Number(value), currency)}
          />
        ) : null}
        <ChartTooltip cursor={{ opacity: 0.08 }} content={valueTooltip(currency, formatValue)} />
        <Area
          dataKey={series.key}
          type="monotone"
          stroke={`var(--color-${series.key})`}
          strokeWidth={2}
          fill={`url(#${fillId})`}
          isAnimationActive={false}
          dot={false}
        />
      </AreaChart>
    </ChartContainer>
  )
}

export interface DashboardMultiLineChartProps {
  /** One row per x value. A series' value may be `null`, which BREAKS the line rather than dropping to zero. */
  data: Array<Record<string, string | number | null>>
  xKey: string
  series: DashboardChartSeries[]
  /** Passed to the formatters. A currency code for money; whatever the caller's count formatter ignores. */
  unit?: string
  height?: number
  showYAxis?: boolean
  /** Off only for a single-series chart, where the title already says what the line is. */
  showLegend?: boolean
  className?: string
}

/**
 * Several lines over one shared x axis — the shape a trend board needs and the only one this family lacked.
 *
 * `connectNulls` is deliberately OFF. These charts are fed from recorded daily snapshots, where a missing
 * value means the rollup did not run that day; bridging the gap would draw a straight line through days
 * nobody measured and present it as observed history.
 *
 * Colours come from the caller as explicit hex. The `--chart-*` tokens render near-black inside recharts
 * (see `budget-detail-charts.tsx`), so a chart that "just uses the theme" arrives unreadable.
 */
export function DashboardMultiLineChart({
  data,
  xKey,
  series,
  unit = "",
  height = 200,
  showYAxis = true,
  showLegend = true,
  className,
  formatValue,
  formatCompact,
}: DashboardMultiLineChartProps & ChartValueFormatters) {
  return (
    <ChartContainer config={buildConfig(series)} className={cn("aspect-auto w-full", className)} style={{ height }}>
      <LineChart data={data} margin={{ left: 4, right: 4, top: 4, bottom: 0 }}>
        <CartesianGrid vertical={false} strokeOpacity={0.4} />
        <XAxis dataKey={xKey} tickLine={false} axisLine={false} tickMargin={6} minTickGap={24} fontSize={AXIS_FONT} />
        {showYAxis ? (
          <YAxis
            width={44}
            tickLine={false}
            axisLine={false}
            fontSize={AXIS_FONT}
            tickFormatter={(value) => formatCompact(Number(value), unit)}
          />
        ) : null}
        <ChartTooltip cursor={{ opacity: 0.08 }} content={valueTooltip(unit, formatValue)} />
        {/* Named lines, not colour-coded mystery: a tooltip only says which is which once you hover. */}
        {showLegend ? <ChartLegend content={<ChartLegendContent />} verticalAlign="bottom" /> : null}
        {series.map((s) => (
          <Line
            key={s.key}
            dataKey={s.key}
            type="monotone"
            stroke={`var(--color-${s.key})`}
            strokeWidth={2}
            dot={false}
            connectNulls={false}
            isAnimationActive={false}
          />
        ))}
      </LineChart>
    </ChartContainer>
  )
}
